import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {
    BucketName,
    IncidentApiService,
    InquiryInfo,
    ReferenceBookResponse,
    ReferenceBookType,
} from '@npck/af-frontend-api';
import {rxSpinner} from '@npck/libs/commons';
import {combineLatest} from 'rxjs';
import {mapToViewModel} from '../../../../../components/files/file-preview/file-preview.component';
import {AttachmentsForm, createAttachmentsRequest} from '../../../../../components/files/files.component';
import {AuthHolderService} from '../../../../../domain/auth-holder.service';
import {MAX_FILES_COUNT} from '../../../../../domain/constants';
import {DictionaryHolderService} from '../../../../../domain/dictionary-holder.service';
import {AttachmentViewModel} from '../../documents-tab/documents-tab.component';

export const DESCRIPTION_MAX_LENGTH = 2000;

@Component({
    selector: 'af-inquiry-response-panel',
    templateUrl: './inquiry-response-panel.component.html',
    styleUrl: './inquiry-response-panel.component.scss',
    standalone: false,
})
export class InquiryResponsePanelComponent implements OnInit {
    @Input() incidentId: string;
    @Input() inquiryInfo: InquiryInfo;
    @Input() isInquiry: boolean;
    @Input() isDeleted: boolean;
    @Input() hasResponse: boolean;
    @Output() reloadInquiriesListener = new EventEmitter<void>();
    DESCRIPTION_MAX_LENGTH = DESCRIPTION_MAX_LENGTH;
    MAX_FILES_COUNT = MAX_FILES_COUNT;
    form: FormGroup<ResponseForm>;
    senderOrganizationInfo: ReferenceBookResponse;
    receiverOrganizationInfo: ReferenceBookResponse;
    shouldShowDetails: boolean;
    attachments: AttachmentViewModel[];
    BucketName = BucketName;

    constructor(
        public authHolderService: AuthHolderService,
        private dictionaryHolderService: DictionaryHolderService,
        private formBuilder: FormBuilder,
        private incidentApiService: IncidentApiService,
    ) {
    }

    ngOnInit(): void {
        this.form = this.formBuilder.group({
            description: new FormControl(null, [Validators.required, Validators.maxLength(DESCRIPTION_MAX_LENGTH)]),
            files: this.formBuilder.group({}),
        });
        combineLatest([
            this.dictionaryHolderService.getReference(ReferenceBookType.BIN),
            this.dictionaryHolderService.getReference(ReferenceBookType.ATTACHED_DOC),
        ])
            .subscribe(([bins, docTypes]) => {
                this.senderOrganizationInfo = bins.find(item => item.code === this.inquiryInfo.sender.bin);
                this.receiverOrganizationInfo = bins.find(item => item.code === this.inquiryInfo.receiver.bin);
                this.attachments = this.inquiryInfo.additionalData?.attachments?.map(attachment => mapToViewModel(attachment, docTypes));
            });
    }

    sendResponse(): void {
        this.incidentApiService.sendResponseToInquiry(this.incidentId, this.inquiryInfo.id, {
            additionalData: {
                description: this.form.value.description,
                attachments: createAttachmentsRequest(this.form.controls.files.value),
            },
        })
            .pipe(rxSpinner(`sendResponse_${this.inquiryInfo.id}`))
            .subscribe(() => this.reloadInquiriesListener.emit());
    }

    sendRead(): void {
        this.shouldShowDetails = !this.shouldShowDetails;
        const isReceiver = this.inquiryInfo.receiver.bin === this.authHolderService.organizationBin;
        const shouldSendRead = this.inquiryInfo.isUnread && isReceiver && this.shouldShowDetails;
        if (shouldSendRead) {
            this.incidentApiService.markInquiryRead(this.incidentId, this.inquiryInfo.id)
                .subscribe(() => this.inquiryInfo.isUnread = false);
        }
    }
}

interface ResponseForm {
    description: FormControl<string>;
    files: FormGroup<AttachmentsForm>;
}
